<script setup lang="ts">
    import { computed, onMounted, watch, shallowReactive, type Ref } from 'vue'
    import { useRoute } from 'vue-router'

    import CorporateNavigation from '@common/Components/CorporateNavigation.vue'
    import { MonthPickerDialogViewModel } from '@common/Components/MonthPickerDialog'
    import MonthPickerDialog from '@common/Components/MonthPickerDialog.vue'
    import { MonthYear, MonthYearHelpers, ValidationError } from '@common/types'
    import { usePortfolioSettingsQueryV1 } from '@Quest/Portfolios/queries-vue-composables'
    import { useUpdatePortfolioSettingsCommandV1 } from '@Quest/Portfolios/commands-vue-composables'

    import type {
        LinkableItemReadModel,
        PortfolioSettingsReadModel
    } from '@Quest/Portfolios/readmodels'


    // ======================
    // Route
    // ======================

    const route = useRoute()

    const workspaceId = computed(() => Number(route.params.workspaceId ?? 0))
    const tchProjectId = computed(() => Number(route.params.tchProjectId ?? 0))


    // ======================
    // Query
    // ======================

    const {
        runPortfolioSettingsQueryV1,
        modelPortfolioSettingsQueryV1
    } = usePortfolioSettingsQueryV1(workspaceId, tchProjectId)

    const rm = modelPortfolioSettingsQueryV1 as Ref<PortfolioSettingsReadModel | null>
    const strategyId = computed(() => rm.value?.StrategyId ?? '')
    const portfolioId = computed<string | null>(() => rm.value?.Id ?? null)


    // ======================
    // Command
    // ======================

    const {
        invokeUpdatePortfolioSettingsCommandV1,
        onSucceedUpdatePortfolioSettingsCommandV1,
        invokingUpdatePortfolioSettingsCommandV1,
        validationErrorsUpdatePortfolioSettingsCommandV1
    } = useUpdatePortfolioSettingsCommandV1(workspaceId, portfolioId)


    // ======================
    // State
    // ======================

    const name = computed({
        get: () => rm.value?.Name ?? null,
        set: (val: string | null) =>
        {
            if (rm.value) rm.value.Name = val
        }
    })

    const description = computed({
        get: () => rm.value?.Description ?? null,
        set: (val: string | null) =>
        {
            if (rm.value) rm.value.Description = val
        }
    })

    const benefitsConfidenceDate = computed({
        get: () => rm.value?.BenefitsConfidenceDate ?? null,
        set: (val: MonthYear | null) =>
        {
            if (rm.value) rm.value.BenefitsConfidenceDate = val
        }
    })


    const benefitsConfidenceDateMonthPicker = shallowReactive(new MonthPickerDialogViewModel())
    const nameError = computed(() => validationErrorsUpdatePortfolioSettingsCommandV1.value?.find(error => error.Context === 'Name')?.Error ?? null)
    const benefitsConfidenceDateLabel = computed(() => MonthYearHelpers.toMonthYearLongString(benefitsConfidenceDate.value))


    // ======================
    // Methods
    // ======================

    function toggleLinkage(i: LinkableItemReadModel)
    {
        i.IsLinked = !i.IsLinked
    }

    function showBenefitsConfidenceDateMonthPicker()
    {
        benefitsConfidenceDateMonthPicker.show(
            benefitsConfidenceDate.value,
            'End of benefits confidence',
            false
        )
    }

    function handleUpdateBenefitsConfidenceDate(date: MonthYear | null)
    {
        benefitsConfidenceDate.value = date
    }

    function handleUpdatePortfolioSettingsCommand()
    {
        if (!rm.value) return

        const excludedResourceIds =
            rm.value.Resources.filter(a => !a.IsLinked).map(a => a.Id)

        const excludedRevenueIds =
            rm.value.Revenues.filter(a => !a.IsLinked).map(a => a.Id)

        const excludedExpenseIds =
            rm.value.Expenses.filter(a => !a.IsLinked).map(a => a.Id)

        const excludedCapexIds =
            rm.value.Capex.filter(a => !a.IsLinked).map(a => a.Id)

        invokeUpdatePortfolioSettingsCommandV1({
            name: name.value!,
            description: description.value,
            benefitsConfidenceDate: benefitsConfidenceDate.value,
            excludedResourceIds,
            excludedRevenueIds,
            excludedExpenseIds,
            excludedCapexIds
        })
    }


    // ======================
    // Lifecycle
    // ======================

    onMounted(() =>
    {
        document.title = 'Portfolio | TransparentChoice'
        runPortfolioSettingsQueryV1()
    })

    onSucceedUpdatePortfolioSettingsCommandV1(() =>
    {
        alert('Portfolio settings updated successfully')
        runPortfolioSettingsQueryV1()
    })


    watch(validationErrorsUpdatePortfolioSettingsCommandV1, (current, previous) =>
    {
        if (current?.length && current !== previous)
        {
            alert('Sth went wrong')
        }
    })
</script>

<template>
    <div>
        <MonthPickerDialog :viewModel="benefitsConfidenceDateMonthPicker" 
                           @update-date="handleUpdateBenefitsConfidenceDate" />

        <CorporateNavigation v-if="rm" ActivePage="portfolios" :propStrategyId="strategyId" />

        <div v-if="rm" class="container" style="margin-top: 80px; padding-top: 40px;">
            <div class="row">
                <div class="col-lg-6">
                    <h1 style="font-size: 2em; font-weight: 300; color: #33475b; margin: 0;">Settings</h1>
                </div>
                <div class="col-lg-6 text-right align-self-end">
                    <button type="submit" :disabled="invokingUpdatePortfolioSettingsCommandV1"
                            class="btn btn-outline-secondary" style="padding: 0.7em 2em;" form="portfolio-settings-form">Save settings</button>
                </div>
            </div>

            <hr style="border-color: #bbb; margin: 20px 0 60px;" />

            <form id="portfolio-settings-form" @submit.prevent="handleUpdatePortfolioSettingsCommand">

                <div class="form-group">
                    <label>Portfolio name:</label>
                    <input v-model.trim="name" type="text" class="form-control" required maxlength="100" minlength="2">
                    <div class="error">{{nameError}}</div>
                </div>

                <div class="form-group">
                    <label>Description:</label>
                    <textarea v-model.trim="description" class="form-control" />
                </div>


                <div class="form-row">
                    <div class="col-lg-6">
                        <div class="form-group">
                            <label>End of benefits confidence:</label>
                            <div class="input-group" v-if="benefitsConfidenceDate == null">
                                <input :value="`Not defined`" type="text" class="form-control" readonly />
                                <div class="input-group-append">
                                    <button class="btn btn-outline-secondary" type="button" @click="showBenefitsConfidenceDateMonthPicker">
                                        <i class="fa-regular fa-calendar" aria-hidden="true"></i>
                                    </button>
                                </div>
                            </div>
                            <div class="input-group" v-if="benefitsConfidenceDate != null">
                                <input :value="benefitsConfidenceDateLabel" type="text" class="form-control" readonly />
                                <div class="input-group-append">
                                    <button class="btn btn-outline-secondary" type="button" @click="showBenefitsConfidenceDateMonthPicker">
                                        <i class="fa-regular fa-calendar" aria-hidden="true"></i>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>


                <br />
                <h4>Revenues:</h4>
                <div class="row mt-3">
                    <div class="col-md-4 col-lg-4 mt-1" v-for="r in rm.Revenues" :key="r.Id">
                        <button type="button" class="btn btn-block text-left" :class="r.IsLinked ? 'btn-outline-success' : 'btn-light'" @click="toggleLinkage(r)">
                            <i class="fa-regular" :class="r.IsLinked ? 'fa-square-check' : 'fa-square'"></i>
                            {{r.Name}}
                        </button>
                    </div>
                </div>

                <br />
                <h4>Resources:</h4>
                <div class="row mt-3">
                    <div class="col-md-4 col-lg-4 mt-1" v-for="r in rm.Resources" :key="r.Id">
                        <button type="button" class="btn btn-block text-left" :class="r.IsLinked ? 'btn-outline-success' : 'btn-light'" @click="toggleLinkage(r)">
                            <i class="fa-regular" :class="r.IsLinked ? 'fa-square-check' : 'fa-square'"></i>
                            {{r.Name}}
                        </button>
                    </div>
                </div>

                <br />
                <h4>Expense accounts:</h4>
                <div class="row mt-3">
                    <div class="col-md-4 col-lg-4 mt-1" v-for="r in rm.Expenses" :key="r.Id">
                        <button type="button" class="btn btn-block text-left" :class="r.IsLinked ? 'btn-outline-success' : 'btn-light'" @click="toggleLinkage(r)">
                            <i class="fa-regular" :class="r.IsLinked ? 'fa-square-check' : 'fa-square'"></i>
                            {{r.Name}}
                        </button>
                    </div>
                </div>

                <br />
                <h4>Capex accounts:</h4>
                <div class="row mt-3">
                    <div class="col-md-4 col-lg-4 mt-1" v-for="r in rm.Capex" :key="r.Id">
                        <button type="button" class="btn btn-block text-left" :class="r.IsLinked ? 'btn-outline-success' : 'btn-light'" @click="toggleLinkage(r)">
                            <i class="fa-regular" :class="r.IsLinked ? 'fa-square-check' : 'fa-square'"></i>
                            {{r.Name}}
                        </button>
                    </div>
                </div>
            </form>
                
            <br />

            <br />
            <br />
            <br />
        </div>
    </div>
</template>